<template>
  <!-- Custom Component -->
  <div v-if="downloadInfo.status && isDesktop">
    <DownloadTrigger platform="desktop" :size="size">
      {{ downloadInfo.name }}
    </DownloadTrigger>
    <GfrDialog
      :show="open"
      class-name="!w-150 bg-[#2b2d32]"
      transition="slide-down"
      placement="top"
      @update:show="onUpdateShow"
      @cancel="onCancel"
      @confirm="onConfirm"
      @closed="onClosed"
    >
      <template #title>
        <GfrTitle class="app-download-title" :level="4" placement="center">
          {{ downloadInfo.popup.title }}
        </GfrTitle>
      </template>
      <template #content>
        <div class="app-download-container">
          <div class="app-download-ctx">
            <GfrScrollBar ref="downloadScroll" class="app-download-scroll" always :reset="resetScroll">
              <div class="app-download-content" v-html="downloadInfo.popup.desc"></div>
            </GfrScrollBar>
          </div>
          <div class="app-download-policy">
            <div v-for="(item, index) in form" :key="index" class="mb-2.5 flex items-start space-x-2 last:mb-0">
              <Checkbox
                :id="item.name"
                :checked="item.value"
                :class="[
                  'mt-0.5 h-4 w-4 data-[state=checked]:text-[#14161B] rtl:ml-2',
                  item.error && 'border-[#FF3838]'
                ]"
                @update:checked="
                  (value) => {
                    item.value = value
                    clearError(item)
                  }
                "
              />
              <label
                :for="item.name"
                class="text-sm peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                v-html="formatLink(item.label)"
              >
              </label>
            </div>
          </div>
          <GfrNewButton :class="['app-download-confirm', validated && 'active']" @click="onConfirm">
            {{ formatTransify('UGC_OFFICIAL_WEBSITE_DOWNLOAD_CONFIRM') }}
          </GfrNewButton>
        </div>
      </template>
    </GfrDialog>
  </div>
</template>
<script lang="ts" setup>
import DownloadTrigger from '~/components/common/Download/Trigger.vue'
import { Checkbox } from '~/components/ui/checkbox'
import { downloadFile } from '~/utils'
const props = defineProps({
  size: {
    type: String as PropType<'normal' | 'large'>,
    default: 'normal'
  }
})
const { isDesktop } = useDevice()
const store = useStore()
const { formatTransify } = store
const { downloadInfo, config } = storeToRefs(store)
const downloadScroll = ref<any>()
const gtag = useGtag()
const open = ref(false)
const resetScroll = ref(false)
const filename = computed(() => {
  const url = downloadInfo.value.url
  return url?.substring(url?.lastIndexOf('/') + 1, url.lastIndexOf('.'))
})
const formatLink = (text: string) => {
  return text.replace(/\{(.*?)_(.*?)}/g, function (_, p1, key) {
    const idx = Number(key) - 1
    const elem = config.value.footer.privacy_policy[idx]
    if (elem) {
      return `<a href="${elem.url}" target="_blank">${elem.name}</a>`
    } else {
      return `{${p1}_${key}}`
    }
  })
}
const form = ref([
  {
    name: 'policy',
    label: formatTransify('UGC_OFFICIAL_WEBSITE_DOWNLOAD_TIPS1'),
    value: false,
    error: false
  },
  {
    name: 'age',
    label: formatTransify('UGC_OFFICIAL_WEBSITE_DOWNLOAD_TIPS2'),
    value: false,
    error: false
  }
])
const validated = computed(() => {
  return form.value.every((item) => item.value)
})
const triggerOpen = () => {
  open.value = true
  resetStatus()
}
const triggerClose = () => (open.value = false)
const triggerDownload = () => {
  gtag('event', 'download_button_click', { event_category: 'Download', event_label: true })
  if (downloadInfo.value.popup.status) {
    gtag('event', 'download_popup_show', { event_category: 'Download', event_label: true })
    triggerOpen()
  } else {
    gtag('event', 'download_client', { event_category: 'Download', event_label: true })
    download()
  }
}
const download = () => {
  // downloadInfo.value.url && downloadFile(downloadInfo.value.url, filename.value)
  if (downloadInfo.value.url) {
    downloadFile(downloadInfo.value.url, filename.value)
  }
}
const onUpdateShow = (value: boolean) => {
  open.value = value
}
const onConfirm = () => {
  if (!validForm()) return
  gtag('event', 'download_popup_confirm', { event_category: 'Download', event_label: true })
  download()
  triggerClose()
  gtag('event', 'download_client', { event_category: 'Download', event_label: true })
}
const onCancel = () => {
  gtag('event', 'download_popup_cancel', { event_category: 'Download', event_label: true })
  triggerClose()
}
const onClosed = () => {
  resetScroll.value = false
}
const clearError = (item: any) => {
  item.error = false
}
const validForm = () => {
  if (!validated.value) {
    form.value.forEach((item) => {
      item.error = !item.value
    })
  }
  return validated.value
}
const resetStatus = async () => {
  await nextTick()
  resetScroll.value = true
  form.value.forEach((item) => {
    item.value = false
    item.error = false
  })
}
provide('triggerDownload', triggerDownload)
</script>
<style lang="scss" scoped>
@import './styles/index';
</style>
